import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EditOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';

import { useAudience } from 'api/audiences';
import { StatusTag, TableActions, WrapperPanel } from 'components';
import AudienceFilters from 'components/AudienceInfo/AudienceInfoFilters';
import AuditLog from 'components/AuditLog';
import RenderInGrid from 'components/Layout/RenderInGrid';
import { PageContent, PageHeader } from 'components/page';
import { usePermissions } from 'hooks';
import API from 'utils/request';

interface Props {}

const AudienceShowPage = (props: Props) => {
  const { id: segmentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canEdit } = usePermissions('audiences');
  const audienceNumberId = segmentId ? Number(segmentId) : null;
  const { data } = useAudience(audienceNumberId);

  const goToEditPage = () => {
    navigate(`/p/audiences/edit/${segmentId}`);
  };

  const handleExport = () => {
    API.post(`/v4/dash/v4_audiences/${segmentId}/export`)
      .then(() => {
        message.info('The exported file will be sent to your email.');
      })
      .catch(() => {
        message.error('Error exporting audience.');
      });
  };

  return (
    <>
      <PageHeader
        title={data?.name}
        entity_id={data?.id}
        customAction={() => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TableActions
              actions={[
                {
                  title: 'Export',
                  onClick: handleExport
                }
              ]}
            />
            {canEdit && (
              <Button
                icon={<EditOutlined />}
                type="primary"
                size="large"
                onClick={goToEditPage}
                style={{ marginLeft: 16 }}
              >
                Edit
              </Button>
            )}
          </div>
        )}
      />
      <PageContent>
        <WrapperPanel title="Essentials">
          <RenderInGrid description="Status" content={<StatusTag state={data?.state} />} />
          <RenderInGrid description="Audience name" content={data?.name} />
        </WrapperPanel>
        <WrapperPanel title="File Upload & Filters">
          <RenderInGrid
            description="Files uploaded"
            content={
              <div>
                <div>
                  Include:{' '}
                  {data?.user_groups
                    .filter(group => group.list_type === 'allow_list')
                    .map(group => group.name)
                    .join(', ') || '–'}
                </div>
                <div>
                  Exclude:{' '}
                  {data?.user_groups
                    .filter(group => group.list_type === 'deny_list')
                    .map(group => group.name)
                    .join(', ') || '–'}
                </div>
              </div>
            }
          />
          <RenderInGrid description="Filters" content={<AudienceFilters data={data} />} />
        </WrapperPanel>
        <AuditLog entity="v4_audiences" id={segmentId} isChanges2 permissionSection="audiences" />
      </PageContent>
    </>
  );
};

export default AudienceShowPage;
